import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, FileText, Loader2, Clipboard } from "lucide-react";
import mammoth from "mammoth";
// pdfjs-dist is now imported dynamically in processFile to avoid initialization errors
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface FileUploadZoneProps {
  onImport: (content: string) => void;
}

export function FileUploadZone({ onImport }: FileUploadZoneProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const processFile = useCallback(async (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (!extension || !['txt', 'docx', 'pdf'].includes(extension)) {
      toast({
        title: t("editor.upload.invalidFile"),
        description: t("editor.upload.supportedFormats"),
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      if (extension === 'txt') {
        const text = await file.text();
        onImport(text);
        toast({
          title: t("editor.upload.success"),
          description: file.name,
        });
      } else if (extension === 'docx') {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        onImport(result.value);
        toast({
          title: t("editor.upload.success"),
          description: file.name,
        });
      } else if (extension === 'pdf') {
        // Dynamic import for pdfjs-dist to avoid top-level initialization issues
        const pdfjsLib = await import('pdfjs-dist');
        
        // Configure worker
        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
        }

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const textParts: string[] = [];
        
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          // Filter items that have 'str' property and extract text
          const pageText = textContent.items
            .filter((item) => 'str' in item && typeof (item as { str: string }).str === 'string')
            .map((item) => (item as { str: string }).str)
            .join(' ');
          textParts.push(pageText);
        }
        
        onImport(textParts.join('\n\n'));
        toast({
          title: t("editor.upload.success"),
          description: file.name,
        });
      }
    } catch (error) {
      console.error('File processing error:', error);
      toast({
        title: t("editor.upload.error"),
        description: t("editor.upload.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  }, [onImport, toast, t]);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim()) {
        onImport(text);
        toast({
          title: t("editor.upload.pasteSuccess"),
          description: t("editor.upload.pasteSuccessDesc"),
        });
      } else {
        toast({
          title: t("editor.upload.pasteEmpty"),
          description: t("editor.upload.pasteEmptyDesc"),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Clipboard read error:', error);
      toast({
        title: t("editor.upload.pasteError"),
        description: t("editor.upload.pasteErrorDesc"),
        variant: "destructive",
      });
    }
  }, [onImport, toast, t]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  }, [processFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
    e.target.value = '';
  }, [processFile]);

  return (
    <Card 
      className={cn(
        "border-2 border-dashed border-black rounded-xl transition-all",
        isDragging ? "bg-[#FFD93D]/20" : "bg-white",
        isProcessing && "opacity-50 pointer-events-none",
        "shadow-[4px_4px_0_#1A1A1A] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none active:translate-x-1 active:translate-y-1"
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        {isProcessing ? (
          <>
            <Loader2 className="h-12 w-12 text-primary mb-4 animate-spin" />
            <p className="text-muted-foreground">{t("editor.upload.processing")}</p>
          </>
        ) : (
          <>
            <div className={cn(
              "rounded-full p-4 mb-4 transition-colors",
              isDragging ? "bg-primary/10" : "bg-muted"
            )}>
              {isDragging ? (
                <FileText className="h-8 w-8 text-primary" />
              ) : (
                <Upload className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <p className="text-lg font-medium mb-2">
              {isDragging ? t("editor.upload.dropHere") : t("editor.upload.dragDrop")}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {t("editor.upload.supported")}
            </p>
            <div className="flex items-center gap-3">
              <Button variant="outline" asChild className="border-2 border-black bg-white hover:bg-gray-50 hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all duration-100">
                <label className="cursor-pointer">
                  {t("editor.upload.browse")}
                  <input
                    type="file"
                    accept=".txt,.docx,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </Button>
              <Button variant="secondary" onClick={handlePaste} className="border-2 border-black bg-[#FFD93D] hover:bg-[#FFD93D]/80 hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all duration-100">
                <Clipboard className="h-4 w-4 mr-2" />
                {t("editor.upload.paste")}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
